import { Box, CircularProgress, Dialog } from '@material-ui/core';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { z, ZodType } from 'zod';

import { Font } from '@/components/shared/components/Font';
import { FormWrapper, FormWrapperProps } from '@/components/shared/components/FormWrapper';
import { NemuruDialog } from '@/components/shared/components/NemuruDialog';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useFormTools } from '@/components/shared/hooks/useFormTools';
import { isDeepEqual } from '@/utils/isObjectDeepEqual';

interface EditDialogProps<T extends ZodType> {
  opened: boolean;
  title: string;
  subtitle?: string;
  handleClose: () => void;
  dialogProps?: Partial<typeof Dialog>;
  onSubmitForm: (data: z.infer<T>) => void;
  setOpened: (boolean) => void;
  saveButtonTextId?: string;
  isLoading?: boolean;
  forceEnableSaveButton?: boolean;
  defaultValues?: z.infer<T>;
  children?: React.ReactElement;
}

interface EditDialogWrapperProps<T extends ZodType> extends FormWrapperProps<T>, EditDialogProps<T> {}

export const EditDialogWrapper = <T extends ZodType>({
  formSchema,
  defaultValues,
  formMethodsProps,
  ...props
}: EditDialogWrapperProps<T>) => {
  return (
    <FormWrapper formSchema={formSchema} defaultValues={defaultValues} formMethodsProps={formMethodsProps}>
      <EditDialog defaultValues={defaultValues} {...props} />
    </FormWrapper>
  );
};

const EditDialog = <T extends ZodType>({
  opened,
  title,
  subtitle,
  dialogProps,
  handleClose,
  onSubmitForm,
  setOpened,
  saveButtonTextId = 'SAVE',
  isLoading,
  forceEnableSaveButton,
  defaultValues,
  children = <></>,
}: EditDialogProps<T>) => {
  const {
    handleSubmit,
    getValues,
    reset,
    formState: { isDirty, defaultValues: stateDefaultValues },
  } = useFormContext();

  useEffect(() => {
    !isLoading && reset(defaultValues);
  }, [defaultValues]);

  const { disableSaveButton, setDisableSaveButtonState } = useFormTools();

  useEffect(() => {
    if (defaultValues && stateDefaultValues) {
      if (!isDeepEqual(defaultValues, stateDefaultValues)) {
        setDisableSaveButtonState(false);
      }
    }
  }, [stateDefaultValues]);

  useEffect(() => {
    setDisableSaveButtonState(!isDirty);
  }, [isDirty]);

  const handleSave = () => {
    handleSubmit(() => onSubmitForm(getValues()))();
  };

  return (
    <NemuruDialog
      avoidDefaultClose
      opened={opened}
      setOpened={setOpened}
      buttons={
        <>
          <StandardButton disabled={isLoading} onClick={handleClose}>
            <Text tId="CANCEL" />
          </StandardButton>
          <StandardButton
            disabled={!forceEnableSaveButton && (isLoading || disableSaveButton)}
            main
            onClick={handleSave}
          >
            {isLoading ? <CircularProgress size={20} color={'secondary'} /> : <Text tId={saveButtonTextId} />}
          </StandardButton>
        </>
      }
      {...dialogProps}
    >
      <>
        <Box marginBottom={5}>
          <Font variant={'h3'} color={'title'}>
            <Text tId={title} />
          </Font>
          {subtitle && (
            <Box mt={1}>
              <Font variant={'body2'} color={'textSecondary'}>
                <Text tId={subtitle} />
              </Font>
            </Box>
          )}
        </Box>
        {children}
      </>
    </NemuruDialog>
  );
};
